import { useState } from "react";
import { Plus, Minus, HelpCircle, Phone, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";

/**
 * ⚠️ 10Web AI WARNING — DO NOT REPLACE <Icon /> JSX with <img> tags.
 * All lucide icons here are wrapped with data-icon-container="lucide-do-not-replace".
 * Keep them as React components.
 */

const NAVY = "#081438";
const NAVY_DEEP = "#050D24";
const NAVY_LIGHT = "#243752";
const GOLD = "#F5A623";
const GOLD_DARK = "#9A6400";
const FONT_HEAD = "'Plus Jakarta Sans', system-ui, sans-serif";
const FONT_BODY = "'Manrope', system-ui, sans-serif";
const FAQS = [{
  q: "How long does it take to program a new remote in Sydney?",
  a: "Most remote programming visits take 5–15 minutes on-site. Including travel and walkthrough, plan for about 20 minutes total. If you've also asked for a keypad install or a smart Wi-Fi opener setup, allow up to an hour."
}, {
  q: "Will my existing remotes still work after you program a new one?",
  a: "Yes — adding a new remote does not delete the ones already paired. Your existing remotes keep working exactly the same. We only erase remotes if you specifically ask us to (e.g. after buying the house, or losing one you're worried about)."
}, {
  q: "Do I need to know my garage motor model before I call?",
  a: "Not at all. A photo of the motor head (the box on the ceiling) sent via WhatsApp is enough — we'll identify the brand and recommend the right remote. Most Sydney homes have B&D, Merlin, Chamberlain, ATA or Steel-Line, all of which we stock."
}, {
  q: "Can you sync my car's HomeLink or built-in visor button?",
  a: "Yes. We regularly pair HomeLink buttons in BMW, Audi, Tesla, Mercedes, Toyota and Lexus vehicles to garage motors. Bring the car onto the driveway during the visit so we can test the range."
}, {
  q: "What if my garage motor is really old or the brand is discontinued?",
  a: "We carry universal remotes (ATA TrioCodeGDO, LiftMaster Universal, Centsys) that pair with the vast majority of legacy motors — including doors 15+ years old. If your motor genuinely can't accept a new remote, we'll tell you honestly and quote a motor swap as a separate option."
}, {
  q: "How much does a wireless keypad install cost?",
  a: "Keypad supply and install is a flat all-in price, quoted on the phone before we visit. It includes the keypad, programming, wall-mounting, PIN setup and a quick demo so you can change the PIN yourself in future."
}, {
  q: "Can I program a remote myself using a YouTube tutorial?",
  a: "Sometimes — if your motor is a common model, your remote is brand-matched, and the tutorial is for the exact same firmware. The issue is that pairing a wrong-coded remote can lock the motor for 30+ minutes while it cools, and some brands (e.g. Merlin Security+ 2.0) require a specific button-press sequence that's easy to get wrong. If you've tried twice and it hasn't worked, call us."
}, {
  q: "Can you set up Wi-Fi or smart-home garage door control?",
  a: "Yes — we install and commission MyQ (Chamberlain / LiftMaster), Merlin MyQ, B&D Smart Phone Control and most major smart-home hubs. You'll be able to open and close from your phone, get alerts when the door's left open, and share access with family."
}];
export default function RemoteFAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const schemaJson = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQS.map(f => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: f.a
      }
    }))
  };
  return <section className="relative overflow-hidden" style={{
    backgroundColor: NAVY,
    fontFamily: FONT_BODY,
    paddingTop: "110px",
    paddingBottom: "110px"
  }}>
      {/* SEO: FAQ Schema */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(schemaJson)
    }} />

      {/* Ambient radial */}
      <div aria-hidden className="absolute inset-0" style={{
      background: `radial-gradient(circle at 10% 0%, ${NAVY_LIGHT}55 0%, ${NAVY} 50%), radial-gradient(circle at 90% 100%, ${GOLD}10 0%, transparent 40%)`
    }} />

      {/* Grain */}
      <div aria-hidden className="absolute inset-0 opacity-[0.05] pointer-events-none" style={{
      backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence baseFrequency='0.9'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.6'/%3E%3C/svg%3E\")"
    }} />

      {/* Top gold scanline */}
      <div aria-hidden className="absolute top-0 left-0 right-0 h-px" style={{
      background: `linear-gradient(90deg, transparent, ${GOLD}66 50%, transparent)`
    }} />

      {/* Watermark */}
      <div aria-hidden className="absolute pointer-events-none select-none" style={{
      right: "-30px",
      top: "40px",
      fontFamily: FONT_HEAD,
      fontSize: "240px",
      fontWeight: 800,
      lineHeight: 1,
      letterSpacing: "-0.05em",
      color: `${GOLD}12`
    }}>
        09
      </div>

      <div className="relative max-w-[1180px] mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-[0.85fr_1.15fr] gap-12 lg:gap-16 items-start">
          {/* LEFT — sticky header */}
          <div className="lg:sticky lg:top-28">
            <div className="mb-5 flex items-center gap-3">
              <div aria-hidden className="h-px w-12" style={{
              backgroundColor: GOLD
            }} />
              <span style={{
              fontFamily: FONT_HEAD,
              fontSize: "12px",
              fontWeight: 700,
              letterSpacing: "0.22em",
              textTransform: "uppercase",
              color: GOLD
            }}>
                09 · Remote &amp; Keypad FAQ
              </span>
            </div>

            <h2 style={{
            fontFamily: FONT_HEAD,
            fontWeight: 800,
            fontSize: "clamp(2.1rem, 4vw, 3.4rem)",
            lineHeight: 1.05,
            letterSpacing: "-0.025em",
            color: "#ffffff",
            marginBottom: "20px"
          }}>
              The questions we get
              <br />
              <span style={{
              color: GOLD
            }}>every single week</span>.
            </h2>

            <p style={{
            fontFamily: FONT_BODY,
            fontSize: "16px",
            lineHeight: 1.7,
            color: "rgba(255,255,255,0.72)",
            maxWidth: "420px",
            marginBottom: "32px"
          }}>
              Eight quick answers — pulled straight from real calls with Sydney
              homeowners. Don't see yours here? Call and ask.
            </p>

            {/* Help card */}
            <div className="rounded-2xl p-6 relative overflow-hidden" style={{
            background: `linear-gradient(135deg, ${NAVY_LIGHT}AA 0%, ${NAVY_DEEP} 100%)`,
            border: `1px solid ${GOLD}33`
          }}>
              <div className="flex items-start gap-4 mb-5">
                <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex items-center justify-center rounded-lg flex-shrink-0" style={{
                width: 44,
                height: 44,
                backgroundColor: `${GOLD}22`,
                border: `1px solid ${GOLD}44`
              }}>
                  <HelpCircle size={20} color={GOLD} strokeWidth={2.5} />
                </span>
                <div>
                  <h3 style={{
                  fontFamily: FONT_HEAD,
                  fontWeight: 800,
                  fontSize: "1.05rem",
                  color: "#ffffff",
                  letterSpacing: "-0.01em",
                  marginBottom: "4px"
                }}>
                    Still unsure?
                  </h3>
                  <p style={{
                  fontFamily: FONT_BODY,
                  fontSize: "13px",
                  color: "rgba(255,255,255,0.7)",
                  lineHeight: 1.55
                }}>
                    Send a photo of your motor on WhatsApp — we'll tell you
                    the brand and what you need.
                  </p>
                </div>
              </div>

              <div className="flex flex-col gap-2.5">
                <a href="tel:0485000933" className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full transition-all hover:scale-[1.02]" style={{
                backgroundColor: GOLD,
                color: NAVY,
                fontFamily: FONT_HEAD,
                fontWeight: 700,
                fontSize: "13px",
                boxShadow: `0 6px 18px ${GOLD}33`
              }}>
                  <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex">
                    <Phone size={14} strokeWidth={2.5} />
                  </span>
                  Call 0485 000 933
                </a>

                <a href="https://wa.me/61485000933?text=Hi%2C%20I%27d%20like%20to%20send%20a%20photo%20of%20my%20garage%20motor%20for%20a%20quick%20quote." target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full transition-all hover:scale-[1.02]" style={{
                backgroundColor: "#25D366",
                color: "#081438",
                fontFamily: FONT_HEAD,
                fontWeight: 700,
                fontSize: "13px"
              }}>
                  WhatsApp a photo
                  <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex">
                    <ArrowRight size={13} strokeWidth={2.5} />
                  </span>
                </a>
              </div>
            </div>
          </div>

          {/* RIGHT — accordion */}
          <div>
            <ul className="space-y-3">
              {FAQS.map((faq, i) => {
              const isOpen = openIndex === i;
              return <li key={faq.q} className="rounded-xl overflow-hidden transition-all" style={{
                backgroundColor: isOpen ? `${NAVY_LIGHT}88` : `${NAVY_LIGHT}33`,
                border: `1px solid ${isOpen ? `${GOLD}44` : "rgba(255,255,255,0.08)"}`
              }}>
                    <button type="button" onClick={() => setOpenIndex(isOpen ? null : i)} className="w-full text-left flex items-start justify-between gap-4 p-5 md:p-6 transition-colors" aria-expanded={isOpen}>
                      <div className="flex items-start gap-4 flex-1">
                        {/* Q number */}
                        <span className="flex-shrink-0" style={{
                      fontFamily: FONT_HEAD,
                      fontSize: "13px",
                      fontWeight: 800,
                      color: GOLD,
                      letterSpacing: "0.05em",
                      marginTop: "2px",
                      minWidth: "28px"
                    }}>
                          Q{String(i + 1).padStart(2, "0")}
                        </span>

                        <h3 style={{
                      fontFamily: FONT_HEAD,
                      fontWeight: 700,
                      fontSize: "15.5px",
                      lineHeight: 1.35,
                      color: "#ffffff",
                      letterSpacing: "-0.01em",
                      flex: 1
                    }}>
                          {faq.q}
                        </h3>
                      </div>

                      {/* Toggle icon */}
                      <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex items-center justify-center rounded-full flex-shrink-0 mt-0.5" style={{
                    width: 28,
                    height: 28,
                    backgroundColor: isOpen ? GOLD : "rgba(245,166,35,0.15)",
                    border: `1px solid ${isOpen ? GOLD : `${GOLD}44`}`,
                    transition: "all 0.2s ease"
                  }}>
                        {isOpen ? <Minus size={14} color={NAVY} strokeWidth={3} /> : <Plus size={14} color={GOLD} strokeWidth={3} />}
                      </span>
                    </button>

                    {/* Answer */}
                    {isOpen && <div className="px-5 md:px-6 pb-6" style={{
                  paddingLeft: "70px"
                }}>
                        <div className="pt-1 border-t" style={{
                    borderColor: "rgba(255,255,255,0.08)"
                  }}>
                          <p style={{
                      fontFamily: FONT_BODY,
                      fontSize: "14.5px",
                      lineHeight: 1.7,
                      color: "rgba(255,255,255,0.78)",
                      paddingTop: "16px"
                    }}>
                            {faq.a}
                          </p>
                        </div>
                      </div>}
                  </li>;
            })}
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom gold scanline */}
      <div aria-hidden className="absolute bottom-0 left-0 right-0 h-px" style={{
      background: `linear-gradient(90deg, transparent, ${GOLD}66 50%, transparent)`
    }} />
    </section>;
}